import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  MenuItem,
  FormControl,
  Select,
  Button,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { selectAssets } from 'store/asset.slice';
import { showNotification } from 'store/notification.slice';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  resolveShiftOptions,
  resolveCurrentShiftIndex,
} from 'utils/helpers/customerShiftHelpers';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import ReportAPI, { type MaterialMovementRow } from 'api/report.api';
import CustomerShiftConfigAPI from 'api/customer-shift-config.api';
import {
  setCustomerShiftConfigData,
  setCustomerShiftConfigLoading,
} from 'store/customer-shift-config.slice';

dayjs.extend(utc);
dayjs.extend(timezone);

const LIVE_POLL_INTERVAL_MS = 2 * 60 * 1000;

interface MaterialMovementGridRow extends MaterialMovementRow {
  _gridId: number;
}

interface MaterialMovementReportBaseProps {
  titleKey: string;
  primaryField: 'excavator' | 'unloading_area';
  csvFilenamePrefix: string;
  fetchReport: (payload: {
    customerId: string;
    date?: string;
    shift?: string | string[];
    dateFrom?: string;
    dateTo?: string;
  }) => Promise<MaterialMovementRow[]>;
}

const triggerCsvDownload = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const MaterialMovementReportBase = ({
  titleKey,
  primaryField,
  csvFilenamePrefix,
  fetchReport,
}: MaterialMovementReportBaseProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { customerCode, customerTz } = useCustomerInfo();

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const shiftPeriods = useAppSelector(
    (state) => state.customerShiftConfigReducer.data?.shiftPeriods ?? []
  );
  const shiftConfigCustCode = useAppSelector(
    (state) => state.customerShiftConfigReducer.custCode
  );
  const shiftConfigLoading = useAppSelector(
    (state) => state.customerShiftConfigReducer.loading
  );

  // The shift config is normally only fetched when a user switches customers
  // via the top-bar CustomerSelector — arriving here directly (deep link,
  // page refresh with a persisted customer) can leave it empty, so "current
  // shift" can never resolve. Load it ourselves whenever it's missing or
  // stale for the current customer. This is fire-and-forget: it only
  // dispatches into Redux, it never blocks or delays the Live fetch below —
  // `shiftPeriods.length` in that effect's deps just picks up the correction
  // on its own once this resolves.
  useEffect(() => {
    if (!customerCode || shiftConfigLoading) return;
    if (shiftConfigCustCode?.toLowerCase() === customerCode.toLowerCase())
      return;

    let cancelled = false;
    dispatch(setCustomerShiftConfigLoading(true));
    CustomerShiftConfigAPI.getCustomerShiftConfig(customerCode)
      .then((shiftConfig) => {
        if (cancelled) return;
        dispatch(
          setCustomerShiftConfigData({
            custCode: customerCode,
            data: shiftConfig,
          })
        );
      })
      .catch((err) => {
        console.error('Failed to load customer shift config', err);
        if (!cancelled) dispatch(setCustomerShiftConfigLoading(false));
      });

    return () => {
      cancelled = true;
    };
  }, [customerCode, shiftConfigCustCode, shiftConfigLoading, dispatch]);

  const today = dayjs().format('YYYY-MM-DD');
  const [mode, setMode] = useState<'live' | 'single' | 'range'>('live');
  const [isPolling, setIsPolling] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [liveShiftIndex, setLiveShiftIndex] = useState<number | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const [date, setDate] = useState<string>(today);
  const [shifts, setShifts] = useState<string[]>([]);

  const [rangeStart, setRangeStart] = useState<string>('');
  const [rangeEnd, setRangeEnd] = useState<string>('');

  const [rows, setRows] = useState<MaterialMovementGridRow[]>([]);

  const [excavatorFilter, setExcavatorFilter] = useState<string[]>([]);
  const [unloadingAreaFilter, setUnloadingAreaFilter] = useState<string[]>([]);

  const assets = useAppSelector(selectAssets);

  // The API's `excavator` field is the tracker device id (e.g. "symbotE401245"),
  // not the human-readable bumper number. Map it the same way trucks are
  // mapped in Trip Report (Sensor).
  const deviceIdToBumper = useMemo(() => {
    const map = new Map<string, string>();
    assets.forEach((a) => {
      if (a.device?.deviceId && a.bumperNumber) {
        map.set(a.device.deviceId, a.bumperNumber);
      }
    });
    return map;
  }, [assets]);

  const getBumperNumber = (rawId: string | null | undefined): string =>
    (rawId ? deviceIdToBumper.get(rawId) : undefined) ?? rawId ?? '';

  const uniqueExcavators = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => getBumperNumber(r.excavator)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [rows, deviceIdToBumper]
  );

  const uniqueUnloadingAreas = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.unloading_area).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const excavatorSet = new Set(excavatorFilter.map((f) => f.toLowerCase()));
    const unloadingAreaSet = new Set(
      unloadingAreaFilter.map((f) => f.toLowerCase())
    );

    return rows.filter((r) => {
      const excavatorOk =
        excavatorSet.size === 0 ||
        excavatorSet.has(getBumperNumber(r.excavator).toLowerCase());
      const unloadingAreaOk =
        unloadingAreaSet.size === 0 ||
        unloadingAreaSet.has(r.unloading_area?.toLowerCase() ?? '');
      return excavatorOk && unloadingAreaOk;
    });
  }, [rows, excavatorFilter, unloadingAreaFilter, deviceIdToBumper]);

  const totalTonnage = useMemo(
    () =>
      filteredRows.reduce((sum, r) => sum + (Number(r.total_load_ton) || 0), 0),
    [filteredRows]
  );

  const totalTrips = useMemo(
    () => filteredRows.reduce((sum, r) => sum + (Number(r.trip_count) || 0), 0),
    [filteredRows]
  );

  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [lastGeneratedDate, setLastGeneratedDate] = useState<string | null>(
    null
  );

  const shiftOptions =
    shiftPeriods.length > 0
      ? shiftPeriods
      : [{ shiftName: 'Day Shift' }, { shiftName: 'Night Shift' }];

  const inputOutlineSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.borderGrey },
      '&:hover fieldset': { borderColor: palette.borderGrey },
      '&.Mui-focused fieldset': {
        borderColor: palette.borderGrey,
        borderWidth: 1.5,
      },
      '& .MuiInputBase-input': { color: 'text.primary' },
      '& .MuiSvgIcon-root': { color: 'text.primary' },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      '&.Mui-focused': { color: 'text.primary' },
    },
    '& .MuiInputBase-root': { height: 40 },
  } as const;

  // Same as inputOutlineSx but for multi-select Autocomplete fields — MUI's
  // default multi-select input wraps chips onto new lines, which grows the
  // box taller as more options are selected. `limitTags={1}` already caps
  // what's visible to one chip + "+N", so the box never needs more than one
  // line — force that here (no wrap, hidden overflow) so it can't grow.
  const multiSelectInputSx = {
    ...inputOutlineSx,
    '& .MuiInputBase-root': {
      height: 40,
      flexWrap: 'nowrap',
      overflow: 'hidden',
    },
  } as const;

  const isRangeDayDisabled = (day: Dayjs): boolean => {
    const dayStr = day.format('YYYY-MM-DD');
    const monthAgo = dayjs().subtract(1, 'month').startOf('day');
    if (day.isBefore(monthAgo)) return true;
    if (day.isAfter(dayjs())) return true;
    if (dayStr === today && lastGeneratedDate !== today) return true;
    return false;
  };

  // Every fetch — Single Date, Date Range, and Live — goes through this one
  // call so Live issues the exact same request shape as Single Date
  // (`{ customerId, date, shift }`), just with `date`/`shift` computed
  // automatically instead of picked by hand.
  const runFetch = async (
    payload: {
      date?: string;
      dateFrom?: string;
      dateTo?: string;
      shift: string[];
    },
    options: { silent?: boolean; isLive?: boolean } = {}
  ) => {
    if (!customerId) return;
    const { silent = false, isLive = false } = options;

    if (silent) setIsPolling(true);
    else setLoading(true);

    try {
      const data = await fetchReport({ customerId, ...payload });
      if (!isMountedRef.current) return;
      setRows(data.map((r, i) => ({ ...r, _gridId: i })));

      if (!silent) {
        setExcavatorFilter([]);
        setUnloadingAreaFilter([]);
      }
      if (isLive) setLastUpdatedAt(Date.now());
      if (payload.date) setLastGeneratedDate(payload.date);
      else if (payload.dateTo) setLastGeneratedDate(payload.dateTo);

      setFetched(true);
    } catch (err) {
      console.error('Failed to fetch material movement report', err);
      const message = await ReportAPI.extractErrorMessage(err);
      if (isMountedRef.current) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    } finally {
      if (isMountedRef.current) {
        if (silent) setIsPolling(false);
        else setLoading(false);
      }
    }
  };

  const handleFetch = async () => {
    if (mode === 'single' && (!date || shifts.length === 0)) return;
    if (mode === 'range' && (!rangeStart || !rangeEnd || shifts.length === 0))
      return;

    if (mode === 'single') {
      await runFetch({ date, shift: shifts });
    } else {
      await runFetch({ dateFrom: rangeStart, dateTo: rangeEnd, shift: shifts });
    }
  };

  // Live mode: resolve today's date (customer timezone) and whichever shift is
  // currently active, then call `runFetch` the same way Single Date does.
  // `silent` refreshes never toggle the blocking `loading` flag — the grid
  // keeps showing the last-known rows while the request is in flight, so the
  // screen never appears to hang.
  const fetchLive = async (silent: boolean) => {
    if (!customerTz) return;

    const todayInCustomerTz = dayjs().tz(customerTz).format('YYYY-MM-DD');

    // Resolve the active shift index from boundary hours that are always
    // available synchronously (real config once loaded, otherwise the
    // hardcoded per-customer ShiftDetails map) — never blocked on the async
    // shift-config fetch above. `shiftOptions` (used for the actual API
    // filter value) still prefers real config `shiftName`s once they load;
    // this only decides WHICH index is active right now.
    const boundaryOptions = resolveShiftOptions(
      shiftPeriods,
      customerCode ?? ''
    );
    const activeIndex = resolveCurrentShiftIndex(boundaryOptions, customerTz);

    const currentShiftName =
      activeIndex !== null ? shiftOptions[activeIndex]?.shiftName : undefined;
    const liveShifts = currentShiftName
      ? [currentShiftName]
      : shiftOptions.map((s) => s.shiftName);

    setLiveShiftIndex(activeIndex);
    setDate(todayInCustomerTz);
    setShifts(liveShifts);

    await runFetch(
      { date: todayInCustomerTz, shift: liveShifts },
      { silent, isLive: true }
    );
  };

  useEffect(() => {
    if (mode !== 'live') {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    void fetchLive(false);
    pollIntervalRef.current = setInterval(() => {
      void fetchLive(true);
    }, LIVE_POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [
    mode,
    customerId,
    customerCode,
    customerTz,
    fetchReport,
    shiftPeriods.length,
  ]);

  const excavatorColumn: GridColDef<MaterialMovementGridRow> = {
    field: 'excavator',
    headerName: t('material_movement_report_excavator'),
    width: 180,
    valueFormatter: ({ value }: { value: unknown }) =>
      getBumperNumber(value as string),
  };
  const unloadingAreaColumn: GridColDef<MaterialMovementGridRow> = {
    field: 'unloading_area',
    headerName: t('material_movement_report_unloading_area'),
    width: 200,
  };
  const tripCountColumn: GridColDef<MaterialMovementGridRow> = {
    field: 'trip_count',
    headerName: t('material_movement_report_trip_count'),
    width: 130,
    type: 'number',
  };
  const totalLoadColumn: GridColDef<MaterialMovementGridRow> = {
    field: 'total_load_ton',
    headerName: t('material_movement_report_total_load_ton'),
    width: 170,
    type: 'number',
    valueFormatter: ({ value }: { value: unknown }) =>
      value != null ? Number(value).toLocaleString() : '',
  };

  const columns: Array<GridColDef<MaterialMovementGridRow>> =
    primaryField === 'excavator'
      ? [excavatorColumn, unloadingAreaColumn, tripCountColumn, totalLoadColumn]
      : [
          unloadingAreaColumn,
          excavatorColumn,
          tripCountColumn,
          totalLoadColumn,
        ];

  const canFetch =
    mode === 'single'
      ? Boolean(date && shifts.length > 0 && !loading)
      : Boolean(rangeStart && rangeEnd && shifts.length > 0 && !loading);

  const csvFilename =
    mode === 'live'
      ? `${csvFilenamePrefix}-live-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`
      : mode === 'single'
      ? `${csvFilenamePrefix}-${date}-${shifts
          .map((s) => s.replace(/\s*shift\s*/i, '').trim())
          .join('-')} Shift.csv`
      : `${csvFilenamePrefix}-${rangeStart}-to-${rangeEnd}.csv`;

  const handleDownloadCsv = () => {
    const headers =
      primaryField === 'excavator'
        ? [
            t('material_movement_report_excavator'),
            t('material_movement_report_unloading_area'),
            t('material_movement_report_trip_count'),
            t('material_movement_report_total_load_ton'),
          ]
        : [
            t('material_movement_report_unloading_area'),
            t('material_movement_report_excavator'),
            t('material_movement_report_trip_count'),
            t('material_movement_report_total_load_ton'),
          ];

    const totalRowCells = [
      t('material_movement_report_total_label'),
      '',
      totalTrips,
      totalTonnage,
    ];

    const csvRows = [
      headers.join(','),
      ...filteredRows.map((r) => {
        const excavatorCell = getBumperNumber(r.excavator);
        const unloadingAreaCell = r.unloading_area ?? '';
        const cells =
          primaryField === 'excavator'
            ? [excavatorCell, unloadingAreaCell, r.trip_count, r.total_load_ton]
            : [
                unloadingAreaCell,
                excavatorCell,
                r.trip_count,
                r.total_load_ton,
              ];
        return cells
          .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
          .join(',');
      }),
      totalRowCells.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','),
    ];

    triggerCsvDownload(csvRows.join('\n'), csvFilename);
  };

  const excavatorFilterBox = (
    <Box key="excavator-filter">
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
      >
        {t('material_movement_report_excavator')}
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        limitTags={1}
        size="small"
        disabled={!fetched}
        options={uniqueExcavators}
        value={excavatorFilter}
        onChange={(_, val) => {
          setExcavatorFilter(val);
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            {option}
          </li>
        )}
        sx={{ width: 240 }}
        slotProps={{
          paper: {
            sx: { width: 'max-content', minWidth: 240, maxWidth: 320 },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              excavatorFilter.length === 0
                ? t('material_movement_report_all_excavators')
                : undefined
            }
            sx={multiSelectInputSx}
          />
        )}
      />
    </Box>
  );

  const unloadingAreaFilterBox = (
    <Box key="unloading-area-filter">
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
      >
        {t('material_movement_report_unloading_area')}
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        limitTags={1}
        size="small"
        disabled={!fetched || uniqueUnloadingAreas.length === 0}
        options={uniqueUnloadingAreas}
        value={unloadingAreaFilter}
        onChange={(_, val) => {
          setUnloadingAreaFilter(val);
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            {option}
          </li>
        )}
        sx={{ width: 240 }}
        slotProps={{
          paper: {
            sx: { width: 'max-content', minWidth: 240, maxWidth: 320 },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              unloadingAreaFilter.length === 0
                ? t('material_movement_report_all_unloading_areas')
                : undefined
            }
            sx={multiSelectInputSx}
          />
        )}
      />
    </Box>
  );

  const shiftSelect = (
    <FormControl size="small" sx={{ ...inputOutlineSx, width: 200 }}>
      <Select
        multiple
        value={shifts}
        displayEmpty
        onChange={(e) => {
          const val = e.target.value;
          setShifts(typeof val === 'string' ? val.split(',') : val);
          setFetched(false);
        }}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <Box component="span" sx={{ color: 'text.disabled' }}>
              {t('material_movement_report_select_shifts')}
            </Box>
          ) : (
            selected.join(', ')
          )
        }
      >
        {shiftOptions.map((s) => (
          <MenuItem key={s.shiftName} value={s.shiftName}>
            <Checkbox checked={shifts.includes(s.shiftName)} />
            <ListItemText primary={s.shiftName} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        width: { xs: '100%', md: '80%' },
        flexBasis: { md: '80%' },
        flexGrow: 1,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: 'text.primary', mb: 2.5 }}
      >
        {t(titleKey)}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, val) => {
            if (!val) return;
            setMode(val);
            setFetched(false);
            setRows([]);
            setExcavatorFilter([]);
            setUnloadingAreaFilter([]);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
              fontSize: '0.8rem',
              color: 'text.secondary',
              borderColor: palette.borderGrey,
              '&.Mui-selected': {
                color: 'text.primary',
                bgcolor: enableDarkTheme
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.06)',
              },
            },
          }}
        >
          <ToggleButton value="live">
            {t('material_movement_report_live_tab')}
          </ToggleButton>
          <ToggleButton value="single">
            {t('material_movement_report_single_date')}
          </ToggleButton>
          <ToggleButton value="range">
            {t('material_movement_report_date_range')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Stack
        direction="row"
        alignItems="flex-end"
        sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}
      >
        {mode === 'live' ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 40,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main',
                animation:
                  'material-movement-live-pulse 2s ease-in-out infinite',
                '@keyframes material-movement-live-pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.35 },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: 'success.main',
                textTransform: 'uppercase',
              }}
            >
              {t('material_movement_report_live')}
            </Typography>
            {liveShiftIndex !== null && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('material_movement_report_current_shift', {
                  shift:
                    liveShiftIndex === 0
                      ? t('shift_day')
                      : liveShiftIndex === 1
                      ? t('shift_night')
                      : t('material_movement_report_shift_n', {
                          n: liveShiftIndex + 1,
                        }),
                })}
              </Typography>
            )}
            {lastUpdatedAt && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('material_movement_report_last_updated', {
                  time: dayjs(lastUpdatedAt)
                    .tz(customerTz || dayjs.tz.guess())
                    .format('HH:mm:ss'),
                })}
              </Typography>
            )}
            {isPolling && <CircularProgress size={14} />}
          </Box>
        ) : mode === 'single' ? (
          <>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('material_movement_report_date')}
              </Typography>
              <DatePicker
                value={date ? dayjs(date) : null}
                maxDate={dayjs()}
                onChange={(v) => {
                  setDate(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 180 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('material_movement_report_shift')}
              </Typography>
              {shiftSelect}
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('material_movement_report_from')}
              </Typography>
              <DatePicker
                value={rangeStart ? dayjs(rangeStart) : null}
                maxDate={rangeEnd ? dayjs(rangeEnd) : dayjs()}
                minDate={dayjs().subtract(1, 'month').startOf('day')}
                shouldDisableDate={isRangeDayDisabled}
                onChange={(v) => {
                  setRangeStart(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 160 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('material_movement_report_to')}
              </Typography>
              <DatePicker
                value={rangeEnd ? dayjs(rangeEnd) : null}
                minDate={
                  rangeStart
                    ? dayjs(rangeStart)
                    : dayjs().subtract(1, 'month').startOf('day')
                }
                maxDate={
                  rangeStart
                    ? dayjs(rangeStart).add(1, 'month').isAfter(dayjs())
                      ? dayjs()
                      : dayjs(rangeStart).add(1, 'month')
                    : dayjs()
                }
                shouldDisableDate={isRangeDayDisabled}
                onChange={(v) => {
                  setRangeEnd(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 160 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('material_movement_report_shift')}
              </Typography>
              {shiftSelect}
            </Box>
          </>
        )}

        {mode !== 'live' && (
          <Button
            variant="contained"
            onClick={handleFetch}
            disabled={!canFetch}
            sx={{ textTransform: 'none', height: 40 }}
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('material_movement_report_fetch')
            )}
          </Button>
        )}

        {primaryField === 'excavator'
          ? [excavatorFilterBox, unloadingAreaFilterBox]
          : [unloadingAreaFilterBox, excavatorFilterBox]}
      </Stack>

      {mode === 'live' && loading && !fetched && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 200,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      {fetched && (
        <>
          <Box sx={{ height: 480, width: '100%' }}>
            {filteredRows.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 2 }}
              >
                {t('material_movement_report_no_data')}
              </Typography>
            ) : (
              <DataGrid
                rows={filteredRows}
                columns={columns}
                getRowId={(r) => r._gridId}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                sx={{
                  border: `1px solid ${palette.borderGrey}`,
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: 'background.default',
                  },
                }}
              />
            )}
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <Stack direction="row" spacing={3}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {t('material_movement_report_total_trips', {
                  total: totalTrips.toLocaleString(),
                })}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {t('material_movement_report_total_tonnage', {
                  total: totalTonnage.toLocaleString(),
                })}
              </Typography>
            </Stack>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={filteredRows.length === 0}
              onClick={handleDownloadCsv}
              sx={{
                textTransform: 'none',
                borderColor: palette.borderGrey,
                color: 'text.primary',
                '&:hover': {
                  borderColor: palette.borderGrey,
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {t('material_movement_report_download_csv')}
            </Button>
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default MaterialMovementReportBase;
